import React, { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Plus, Search, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { showDeleteConfirm, showSuccess } from '@/lib/swal'
import { taxRateService } from '@/services/taxRateService'
import type { TaxRateVm } from '@/api/types'

type PageMode = 'list' | 'form'

const yesNo = (value: boolean) => (value ? 'yes' : 'no')
const fromYesNo = (value: string) => value === 'yes'

const emptyForm = (branchId: number): TaxRateVm => ({
  taxRateId: 0,
  taxRateNameEn: '',
  taxRateNameAr: '',
  taxPercent: 0,
  isDefault: false,
  goodsOrService: 1,
  purchaseOrSales: 1,
  branchId,
})

export const Taxes: React.FC = () => {
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [mode, setMode] = useState<PageMode>('list')
  const [rows, setRows] = useState<TaxRateVm[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [form, setForm] = useState<TaxRateVm>(emptyForm(branchId))

  const loadList = async () => {
    if (!branchId) return
    try {
      setLoading(true)
      const res = await taxRateService.getAll(branchId, pageNumber, pageSize)
      setRows(res.data || [])
      setTotalRecords(res.totalRecords || 0)
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to load taxes'
      const isNoData = /not found|no data|no records/i.test(String(message))
      setRows([])
      setTotalRecords(0)
      if (!isNoData) toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadList()
  }, [branchId, pageNumber, pageSize])

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    return rows.filter((row) => {
      return (
        String(row.taxRateId).toLowerCase().includes(q) ||
        row.taxRateNameEn.toLowerCase().includes(q) ||
        row.taxRateNameAr.toLowerCase().includes(q) ||
        String(row.taxPercent).toLowerCase().includes(q)
      )
    })
  }, [rows, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const handleAdd = () => {
    setForm(emptyForm(branchId))
    setMode('form')
  }

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm('Delete Tax', 'Are you sure you want to delete this tax rate?')
    if (!confirmed) return

    try {
      await taxRateService.delete(id)
      await showSuccess('Deleted', 'Tax deleted successfully.')
      await loadList()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to delete tax')
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error('Please select a branch first')
      return
    }
    if (!form.taxRateNameEn.trim()) {
      toast.error('Name is required')
      return
    }
    if (form.taxPercent < 0) {
      toast.error('Percent must be greater than or equal to 0')
      return
    }

    const payload: TaxRateVm = {
      ...form,
      branchId,
      taxPercent: Number(form.taxPercent || 0),
      goodsOrService: Number(form.goodsOrService ?? 0),
      purchaseOrSales: Number(form.purchaseOrSales ?? 0),
    }

    try {
      setSaving(true)
      if (payload.taxRateId > 0) {
        await taxRateService.update(payload)
      } else {
        await taxRateService.save(payload)
      }
      await showSuccess('Saved', 'Tax saved successfully.')
      setMode('list')
      setForm(emptyForm(branchId))
      await loadList()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save tax')
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Taxes" description="Tax Details" />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Please select a branch first.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (mode === 'form') {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <PageHeader title="Taxes" description="Tax Details" />
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={form.taxRateNameEn}
                  onChange={(e) => setForm((prev) => ({ ...prev, taxRateNameEn: e.target.value }))}
                  placeholder="Name"
                />
              </div>

              <div className="space-y-2">
                <Label>Name Ar</Label>
                <Input
                  value={form.taxRateNameAr}
                  onChange={(e) => setForm((prev) => ({ ...prev, taxRateNameAr: e.target.value }))}
                  placeholder="Name Ar"
                />
              </div>

              <div className="space-y-2">
                <Label>Percent</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.taxPercent}
                  onChange={(e) => setForm((prev) => ({ ...prev, taxPercent: Number(e.target.value || 0) }))}
                  placeholder="Percent"
                />
              </div>

              <div className="space-y-2">
                <Label>Is Default</Label>
                <Select
                  value={yesNo(form.isDefault)}
                  onValueChange={(value) => setForm((prev) => ({ ...prev, isDefault: fromYesNo(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Goods / Service</Label>
                <Select
                  value={String(form.goodsOrService ?? 1)}
                  onValueChange={(value) => setForm((prev) => ({ ...prev, goodsOrService: Number(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Goods</SelectItem>
                    <SelectItem value="2">Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Purchase / Sales</Label>
                <Select
                  value={String(form.purchaseOrSales ?? 1)}
                  onValueChange={(value) => setForm((prev) => ({ ...prev, purchaseOrSales: Number(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Purchase</SelectItem>
                    <SelectItem value="2">Sales</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setMode('list')
                  setForm(emptyForm(branchId))
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleSave} disabled={saving}>
                {form.taxRateId > 0 ? 'Update' : 'Save'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Taxes"
        description="Tax Details"
        action={{
          label: 'Add Tax',
          onClick: handleAdd,
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" disabled>
                Column Visibility
              </Button>
              <Button type="button" variant="outline" size="sm" disabled>
                CSV
              </Button>
              <Button type="button" variant="outline" size="sm" disabled>
                Copy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <>
              <Table className="table-fixed">
                <colgroup>
                  <col className="w-20" />
                  <col />
                  <col className="w-32" />
                  <col className="w-24" />
                </colgroup>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">ID</TableHead>
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Tax Rate Name</TableHead>
                    <TableHead className="h-12 px-4 text-right bg-primary/5 font-bold text-primary">Tax Percent</TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">Delete</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                        No data available in table
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow key={row.taxRateId} className="border-b hover:bg-primary/5">
                        <TableCell className="px-4 py-2 text-sm text-center align-middle tabular-nums">
                          {row.taxRateId}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-left align-middle">
                          {row.taxRateNameEn || row.taxRateNameAr || '—'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-right align-middle tabular-nums">
                          {Number(row.taxPercent || 0).toFixed(2)} %
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:bg-destructive/10"
                              onClick={() => handleDelete(row.taxRateId)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  Showing {(pageNumber - 1) * pageSize + 1} to {Math.min(pageNumber * pageSize, totalRecords)} of {totalRecords} entries
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                    disabled={pageNumber <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    {pageNumber} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                    disabled={pageNumber >= totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
